import { Alert, Box, Skeleton, useTheme } from '@mui/material';
import { sanitize } from 'dompurify';
import React from 'react';
import { useParams } from 'react-router-dom';
import { CerebroPageContainer, CerebroBreadcrumb, CerebroSecondaryButton } from '../../shared/components';
import { useDataRoomQuery, useDealQuery } from '../../shared/hooks';
import { CustomNdaForm } from './CustomNdaForm';
import { StandardNda } from './StandardNda';

export const LenderNda: React.FC = () => {
	const theme = useTheme();
	const { guid } = useParams();
	const dataRoomQuery = useDataRoomQuery(guid);
	const dealQuery = useDealQuery(dataRoomQuery.data?.dealGuid, {
		enabled: dataRoomQuery.isSuccess,
		retry: false,
	});
	const [customNda, editCustomNda] = React.useState<boolean>(false);

	const closeAndClearDialogs = () => {
		editCustomNda(false);
	};

	function getCustomAgreementText(text: string) {
		if (text == null || text == '') return null;
		var res = sanitize(text ?? '');
		return res.toString().trim() == '<p></p>' ? null : res;
	}

	return (
		<CerebroPageContainer>
			<CerebroBreadcrumb>Lender Nda</CerebroBreadcrumb>

			{dataRoomQuery.isSuccess && (
				<>
					<Box sx={{ marginBottom: 3 }}>
						<CerebroSecondaryButton sx={{ marginRight: 1 }} onClick={() => editCustomNda(true)}>
							Custom Nda
						</CerebroSecondaryButton>
					</Box>

					{dataRoomQuery.isSuccess && !!getCustomAgreementText(dataRoomQuery.data.customAgreementText) && (
						<>
							<Alert
								severity="info"
								sx={{
									marginBottom: 3,
									boxShadow: '2px 2px 2px 2px ' + theme.palette.primary.main,
									border: 'none',
									backgroundColor: 'white',
									fontWeight: 'bold',
								}}
							>
								This deal has been configured to use the custom NDA as shown below. Lenders will be
								presented with this NDA when accessing the data room, unless a separate NDA has been
								setup in their profile.
							</Alert>
							<div
								dangerouslySetInnerHTML={{
									__html: getCustomAgreementText(dataRoomQuery.data.customAgreementText) ?? '',
								}}
							></div>
						</>
					)}

					{dealQuery.isSuccess && getCustomAgreementText(dataRoomQuery.data.customAgreementText ) == null && (
						<>
							<Alert
								severity="info"
								sx={{
									marginBottom: 3,
									boxShadow: '2px 2px 2px 2px ' + theme.palette.primary.main,
									border: 'none',
									backgroundColor: 'white',
									fontWeight: 'bold',
								}}
							>
								This deal has been configured to use the standard NDA as shown below. Lenders will be
								presented with this NDA when accessing the data room, unless a separate NDA has been
								setup in their profile.
							</Alert>
							<StandardNda dataRoomGuid={guid} companyName={dealQuery.data.companyName} />
						</>
					)}

					{!dataRoomQuery.isSuccess && !dealQuery.isSuccess && <Skeleton height={500} />}

					<CustomNdaForm dataRoom={dataRoomQuery.data} open={customNda} close={closeAndClearDialogs} />
				</>
			)}
		</CerebroPageContainer>
	);
};
